import React, { useState, useEffect } from 'react';
import { eventsAPI } from '../../services/api';
import { Calendar, MapPin, Users, Clock, Filter, Search, RefreshCw, TrendingUp } from 'lucide-react';
import './EventsList.css';

function EventsList() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0); // Force refresh key
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const [filters, setFilters] = useState({
    days: 7,
    category: '',
    minVisitors: 0,
    location: ''
  });

  useEffect(() => {
    loadEvents();
  }, [filters, refreshKey]);

  // Auto-refresh every 2 minutes to ensure fresh data
  useEffect(() => {
    const autoRefreshInterval = setInterval(() => {
      console.log('Auto-refreshing events data...');
      loadEvents(true);
    }, 2 * 60 * 1000); // 2 minutes

    return () => clearInterval(autoRefreshInterval);
  }, []);

  // Page visibility API - refresh when page becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('Page became visible, refreshing data...');
        setRefreshKey(prev => prev + 1);
        loadEvents(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const loadEvents = async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);

      // Clear browser cache if force refresh
      if (forceRefresh) {
        // Clear any potential localStorage cache
        const keys = Object.keys(localStorage);
        keys.forEach(key => {
          if (key.includes('events') || key.includes('cache')) {
            localStorage.removeItem(key);
          }
        });
        
        // Update refresh timestamp
        setLastRefresh(Date.now());
      }

      const queryParams = new URLSearchParams();
      if (filters.days) queryParams.append('days', filters.days);
      if (filters.category) queryParams.append('category', filters.category);
      if (filters.minVisitors) queryParams.append('minVisitors', filters.minVisitors);
      if (filters.location) queryParams.append('location', filters.location);
      if (searchTerm) queryParams.append('search', searchTerm);
      
      // Add cache busting parameters
      queryParams.append('_refresh', lastRefresh);
      queryParams.append('_random', Math.random().toString(36).substr(2, 9));

      const response = await eventsAPI.getUpcoming(queryParams.toString());
      
      if (response.success) {
        // Backend returns { success: true, data: { events: [...], total: N } }
        const eventsData = response.data?.events || response.data || [];
        setEvents(Array.isArray(eventsData) ? eventsData : []);
      } else {
        // Fallback to empty array if API fails
        setEvents([]);
        setError('Events konnten nicht geladen werden');
      }
        } catch (error) {
      console.log('Load events error:', error);
      setError('Offline mode');
      setEvents([]); // Ensure events is always an array
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadEvents();
  };

  const handleViewDetails = (event) => {
    // TODO: Implement event details modal or navigation
    console.log('View details for event:', event);
  };

  const handleNavigateToEvent = (event) => {
    // TODO: Implement navigation to event location
    window.open(`https://maps.google.com/maps?q=${encodeURIComponent(event.location)}`, '_blank');
  };

  const handleSaveEvent = (event) => {
    // TODO: Implement save/bookmark functionality
    console.log('Save event:', event);
  };

  const resetFilters = () => {
    setFilters({
      days: 7,
      category: '',
      minVisitors: 0,
      location: ''
    });
    setSearchTerm('');
  };

  const getDemandLevelInfo = (level) => {
    const levels = {
      'SEHR_HOCH': { color: '#ef4444', label: 'Sehr Hoch', icon: '🔥', gradient: 'from-red-500 to-red-600' },
      'HOCH': { color: '#f97316', label: 'Hoch', icon: '⬆️', gradient: 'from-orange-500 to-orange-600' },
      'MITTEL': { color: '#3b82f6', label: 'Mittel', icon: '➡️', gradient: 'from-blue-500 to-blue-600' },
      'NIEDRIG': { color: '#10b981', label: 'Niedrig', icon: '⬇️', gradient: 'from-green-500 to-green-600' },
      'SEHR_NIEDRIG': { color: '#6b7280', label: 'Sehr Niedrig', icon: '⬇️', gradient: 'from-gray-500 to-gray-600' }
    };
    return levels[level] || levels['NIEDRIG'];
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'MUSIC': { icon: '🎵', color: '#8b5cf6' },
      'SPORTS': { icon: '⚽', color: '#ef4444' },
      'FAIR': { icon: '🏢', color: '#3b82f6' },
      'FESTIVAL': { icon: '🎉', color: '#f59e0b' },
      'THEATER': { icon: '🎭', color: '#ec4899' },
      'CONFERENCE': { icon: '👥', color: '#10b981' },
      'CONCERT': { icon: '🎤', color: '#8b5cf6' }
    };
    return icons[category] || { icon: '📅', color: '#6b7280' };
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeUntilEvent = (dateString) => {
    const eventDate = new Date(dateString);
    const now = new Date();
    const diffHours = Math.round((eventDate - now) / (1000 * 60 * 60));
    
    if (diffHours < 0) return 'Vorbei';
    if (diffHours < 24) return `in ${diffHours}h`;
    const diffDays = Math.round(diffHours / 24);
    return `in ${diffDays} Tag${diffDays > 1 ? 'en' : ''}`;
  };

  if (loading) {
    return (
      <div className="events-loading">
        <div className="loading-content">
          <div className="loading-spinner">
            <RefreshCw className="animate-spin" size={32} />
          </div>
          <h3>Events werden geladen...</h3>
          <p>Einen Moment bitte</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="events-error">
        <div className="error-content">
          <div className="error-icon">⚠️</div>
          <h3>Fehler beim Laden</h3>
          <p>{error}</p>
          <button onClick={() => loadEvents(true)} className="retry-button">
            <RefreshCw size={16} />
            Cache leeren & erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  // Safe array operations with default empty array
  const eventsArray = Array.isArray(events) ? events : [];
  
  // Recalculate stats when data changes or refresh occurs
  const statsData = React.useMemo(() => {
    const totalVisitors = eventsArray.reduce((sum, e) => sum + (e.expected_visitors || 0), 0);
    const avgTaxisPerEvent = eventsArray.length > 0 ? 
      Math.round(eventsArray.reduce((sum, e) => sum + (e.estimatedTaxis || 0), 0) / eventsArray.length) : 0;
    
    return { totalVisitors, avgTaxisPerEvent };
  }, [eventsArray, refreshKey]);

  const { totalVisitors, avgTaxisPerEvent } = statsData;

  return (
    <div className="events-list">
      {/* Header Section */}
      <div className="events-header">
        <div className="header-content">
          <h1>
            <Calendar className="header-icon" />
            Kommende Events
          </h1>
          <p>Entdecke die besten Gelegenheiten für maximale Fahrgäste</p>
        </div>
        
        <div className="header-actions">
          <button 
            onClick={() => {
              setRefreshKey(prev => prev + 1);
              loadEvents(true);
            }} 
            className="refresh-btn"
            disabled={loading}
            title="Cache leeren und Events neu laden"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            {loading ? 'Lädt...' : 'Neu laden'}
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="search-and-filters">
        <form onSubmit={handleSearch} className="search-form">
          <div className="search-input-wrapper">
            <Search className="search-icon" size={20} />
            <input
              type="text"
              placeholder="Events durchsuchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          <button type="submit" className="search-btn">
            Suchen
          </button>
        </form>

        <button 
          className={`filter-toggle ${showFilters ? 'active' : ''}`}
          onClick={() => setShowFilters(!showFilters)}
        >
          <Filter size={16} />
          Filter {showFilters ? 'ausblenden' : 'anzeigen'}
        </button>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="filters-panel">
          <div className="filters-grid">
            <div className="filter-group">
              <label>
                <Clock size={16} />
                Zeitraum
              </label>
              <select 
                value={filters.days} 
                onChange={(e) => handleFilterChange('days', e.target.value)}
              >
                <option value="1">Heute</option>
                <option value="3">Nächste 3 Tage</option>
                <option value="7">Nächste 7 Tage</option>
                <option value="14">Nächste 14 Tage</option>
                <option value="30">Nächste 30 Tage</option>
              </select>
            </div>

            <div className="filter-group">
              <label>
                <TrendingUp size={16} />
                Kategorie
              </label>
              <select 
                value={filters.category} 
                onChange={(e) => handleFilterChange('category', e.target.value)}
              >
                <option value="">Alle Kategorien</option>
                <option value="SPORTS">Sport</option>
                <option value="MUSIC">Musik</option>
                <option value="FESTIVAL">Festival</option>
                <option value="FAIR">Messe</option>
                <option value="THEATER">Theater</option>
                <option value="CONFERENCE">Konferenz</option>
              </select>
            </div>

            <div className="filter-group">
              <label>
                <Users size={16} />
                Min. Besucher
              </label>
              <select 
                value={filters.minVisitors} 
                onChange={(e) => handleFilterChange('minVisitors', e.target.value)}
              >
                <option value="0">Alle</option>
                <option value="100">100+</option>
                <option value="500">500+</option>
                <option value="1000">1.000+</option>
                <option value="5000">5.000+</option>
                <option value="10000">10.000+</option>
              </select>
            </div>

            <div className="filter-group">
              <label>
                <MapPin size={16} />
                Ort
              </label>
              <input 
                type="text"
                placeholder="z.B. Marienplatz"
                value={filters.location}
                onChange={(e) => handleFilterChange('location', e.target.value)}
              />
            </div>
          </div>
          
          <div className="filter-actions">
            <button onClick={resetFilters} className="reset-filters-btn">
              Filter zurücksetzen
            </button>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">📅</div>
          <div className="stat-content">
            <div className="stat-number">{eventsArray.length}</div>
            <div className="stat-label">Events gefunden</div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <div className="stat-number">{totalVisitors.toLocaleString()}</div>
            <div className="stat-label">Erwartete Besucher</div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">🚖</div>
          <div className="stat-content">
            <div className="stat-number">{avgTaxisPerEvent}</div>
            <div className="stat-label">Ø Taxis pro Event</div>
          </div>
        </div>
      </div>

      <div className="events-grid">
        {eventsArray.map(event => {
          const demandInfo = getDemandLevelInfo(event.demandLevel);
          const categoryIcon = getCategoryIcon(event.category);
          
          return (
            <div key={event.id} className="event-card">
              <div className="event-header">
                <div className="event-category">
                  <span className="category-icon">{categoryIcon.icon}</span>
                  <span className="category-text">{event.category}</span>
                </div>
                <div 
                  className="demand-badge" 
                  style={{ backgroundColor: demandInfo.color }}
                >
                  {demandInfo.icon} {demandInfo.label}
                </div>
              </div>

              <div className="event-content">
                <h3 className="event-name">{event.name}</h3>
                <p className="event-description">{event.description}</p>
                
                <div className="event-details">
                  <div className="detail-item">
                    <span className="detail-icon">📅</span>
                    <span>{event.startDateFormatted} um {event.startTime}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-icon">📍</span>
                    <span>{event.location}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-icon">👥</span>
                    <span>{(event.expected_visitors || 0).toLocaleString()} Besucher</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-icon">⏰</span>
                    <span>In {event.hoursUntil || 0} Stunden</span>
                  </div>
                </div>
              </div>

              <div className="event-footer">
                <div className="taxi-info">
                  <span className="taxi-count">🚖 {event.estimatedTaxis || 0} Taxis</span>
                  <span className="taxi-ratio">~{event.estimatedTaxis ? Math.round((event.expected_visitors || 0) / event.estimatedTaxis) : 0} Personen/Taxi</span>
                </div>
                <div className="event-actions">
                  <button className="action-btn details">Details</button>
                  <a href={`/map?event=${event.id}`} className="action-btn navigate">🗺️ Karte</a>
                  <a href={`/weather?lat=${event.latitude}&lon=${event.longitude}`} className="action-btn weather">🌤️ Wetter</a>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {eventsArray.length === 0 && (
        <div className="no-events">
          <h3>🤷‍♂️ Keine Events gefunden</h3>
          <p>Versuche andere Filter oder erweitere den Zeitraum</p>
        </div>
      )}
    </div>
  );
}

// Helper functions
const getCategoryIcon = (category) => {
  const icons = {
    'SPORTS': '⚽',
    'MUSIC': '🎵',
    'FESTIVAL': '🎪',
    'FAIR': '🏢',
    'THEATER': '🎭',
    'CONFERENCE': '📊',
    'OTHER': '📅'
  };
  return icons[category] || icons['OTHER'];
};

const getDemandLevelInfo = (level) => {
  switch(level) {
    case 'HIGH':
      return { label: 'Hoch', color: '#ef4444', icon: '🔥' };
    case 'MEDIUM':
      return { label: 'Mittel', color: '#f59e0b', icon: '⚡' };
    case 'LOW':
      return { label: 'Niedrig', color: '#10b981', icon: '✅' };
    default:
      return { label: 'Unbekannt', color: '#6b7280', icon: '❓' };
  }
};

export default EventsList;
